import { useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import { DropdownMenuLabel } from 'designSystem/components/DropdownMenu'
import { Text } from 'designSystem/components/Text'
import { PageRangeInput } from 'features/knowledgeDocumentViewer/components/toolbar/pdfDownloadMenuOptions.styles'
import { Download } from 'lucide-react'
import { type KeyboardEvent, type ReactNode, useState } from 'react'

type PageRangeControlsProps = {
  totalPages: number
  pdfSignedUrl: string | undefined
  isDownloading: boolean
  onRange: (startPage: number, endPage: number) => void
  actionAriaLabel?: string
  actionIcon?: ReactNode
}

export function PageRangeControls({
  totalPages,
  pdfSignedUrl,
  isDownloading,
  onRange,
  actionAriaLabel,
  actionIcon,
}: PageRangeControlsProps) {
  const { t } = useLingui()
  const [rangeStart, setRangeStart] = useState('')
  const [rangeEnd, setRangeEnd] = useState('')

  const parsedStart = parseInt(rangeStart, 10)
  const parsedEnd = parseInt(rangeEnd, 10)
  const isRangeValid =
    !isNaN(parsedStart) &&
    !isNaN(parsedEnd) &&
    parsedStart >= 1 &&
    parsedEnd <= totalPages &&
    parsedStart <= parsedEnd

  const rangeValidationMessage =
    rangeStart && rangeEnd && !isRangeValid
      ? t`Enter pages between 1 and ${totalPages}`
      : undefined

  const handleRangeKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isRangeValid) {
      onRange(parsedStart, parsedEnd)
    }
    e.stopPropagation()
  }

  return (
    <>
      <DropdownMenuLabel>{t`Page range`}</DropdownMenuLabel>
      <Box
        display='flex'
        align='center'
        gap='xsPlus'
        paddingX='xs'
        paddingY='xs'>
        <PageRangeInput
          type='number'
          inputSize='sm'
          min={1}
          max={totalPages}
          placeholder={t`From`}
          value={rangeStart}
          onChange={(e) => setRangeStart(e.target.value)}
          onKeyDown={handleRangeKeyDown}
        />
        <Text as='span' size='sm' color='muted'>
          {t`to`}
        </Text>
        <PageRangeInput
          type='number'
          inputSize='sm'
          min={1}
          max={totalPages}
          placeholder={t`To`}
          value={rangeEnd}
          onChange={(e) => setRangeEnd(e.target.value)}
          onKeyDown={handleRangeKeyDown}
        />
        <Button
          type='button'
          variant='default'
          size='icon-sm'
          aria-label={actionAriaLabel ?? t`Download page range`}
          onClick={() => onRange(parsedStart, parsedEnd)}
          disabled={!isRangeValid || isDownloading || !pdfSignedUrl}>
          {actionIcon ?? <Download size={14} strokeWidth={2} />}
        </Button>
      </Box>
      {rangeValidationMessage && (
        <Box paddingX='sm' paddingBottom='xs'>
          <Text as='div' size='xs' color='destructive'>
            {rangeValidationMessage}
          </Text>
        </Box>
      )}
    </>
  )
}
